// src/App.js
import React, { useState, useEffect } from 'react'; 
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'; 
import { defaultAboutInfoTemplate } from './api/mockData';


import Home from './pages/Home';
import PostDetail from './pages/PostDetail';
import Archive from './pages/Archive';
import About from './pages/About';
import WritePost from './pages/WritePost';
import NotFound from './pages/NotFound';
import Auth from './pages/Auth';

import { message } from 'antd';

import './App.css';

const LOCAL_STORAGE_POSTS_KEY = 'blogPosts';
const LOCAL_STORAGE_USERS_KEY = 'blogUsers';
const LOCAL_STORAGE_CURRENT_USER_KEY = 'blogCurrentUser';

function App() { 
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [users, setUsers] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null);

  const [aboutInfo, setAboutInfo] = useState({});
  const [aboutLoading, setAboutLoading] = useState(true);

  //初始化时：从 localStorage 加载用户数据和文章数据
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setAboutLoading(true);

      const storedUsers = localStorage.getItem(LOCAL_STORAGE_USERS_KEY);
      if (storedUsers) {
        try {
          setUsers(JSON.parse(storedUsers));
        } catch (e) {
          console.error("Failed to parse users from localStorage, using empty array:", e);
          setUsers([]);
        }
      }

      const storedCurrentUser = localStorage.getItem(LOCAL_STORAGE_CURRENT_USER_KEY);
      if (storedCurrentUser) {
        try {
          const user = JSON.parse(storedCurrentUser);
          setLoggedInUser(user);
          setAboutInfo({ ...defaultAboutInfoTemplate, ...user.aboutInfo });
        } catch (e) {
          console.error("Failed to parse current user from localStorage, setting to null:", e);
          setLoggedInUser(null);
          setAboutInfo(defaultAboutInfoTemplate);
        }
      } else {
        setLoggedInUser(null);
        setAboutInfo(defaultAboutInfoTemplate);
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      let loadedPosts = [];
      const storedPosts = localStorage.getItem(LOCAL_STORAGE_POSTS_KEY);
      if (storedPosts) {
        try {
          loadedPosts = JSON.parse(storedPosts);
        } catch (parseError) {
          console.error("Failed to parse posts from localStorage, using empty array:", parseError);
          loadedPosts = [];
        }
      } else {
        loadedPosts = [];
      }
      setPosts(loadedPosts);

      setLoading(false);
      setAboutLoading(false);
    };
    loadInitialData();
  }, []);

  // 监听 user 和 loggedInUser 变化，同步到 localStorage
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_USERS_KEY, JSON.stringify(users));
  }, [users]);

  useEffect(() => {
    if (loggedInUser) {
      localStorage.setItem(LOCAL_STORAGE_CURRENT_USER_KEY, JSON.stringify(loggedInUser));
      setAboutInfo({ ...defaultAboutInfoTemplate, ...loggedInUser.aboutInfo });
    } else {
      localStorage.removeItem(LOCAL_STORAGE_CURRENT_USER_KEY);
      setAboutInfo(defaultAboutInfoTemplate);
    }
  }, [loggedInUser]);

  // 监听 posts 变化，同步到 localStorage
  useEffect(() => {
    try {
        localStorage.setItem(LOCAL_STORAGE_POSTS_KEY, JSON.stringify(posts));
    } catch (storageError) {
        console.error("Failed to save posts to localStorage:", storageError);
    }
  }, [posts]);


  const handleRegister = (username, password) => {
    if (users.some(user => user.username === username)) {
      return false;
    }
    const newUserAboutInfo = {
        ...defaultAboutInfoTemplate,
        name: username,
        email: `${username}@gmail.com`,
    };
    const newUser = { username, password, aboutInfo: newUserAboutInfo };
    setUsers(prevUsers => [...prevUsers, newUser]);
    return true;
  };

  const handleLogin = (username, password) => {
    const user = users.find(u => u.username === username && u.password === password);
    if (user) {
      setLoggedInUser(user);
      return true;
    }
    return false;
  };

  const handleLogout = () => {
    setLoggedInUser(null);
    message.success('已退出登录。');
  };

  const handleAddPost = (newPost) => {
    setPosts(prevPosts => [newPost, ...prevPosts]);
  };

  const handleUpdateAboutInfo = (updatedInfo) => {
    if (loggedInUser) {
      const mergedAboutInfo = { ...defaultAboutInfoTemplate, ...updatedInfo };
      const updatedLoggedInUser = { ...loggedInUser, aboutInfo: mergedAboutInfo };
      setLoggedInUser(updatedLoggedInUser);
      setUsers(prevUsers => prevUsers.map(user =>
        user.username === loggedInUser.username ? updatedLoggedInUser : user
      ));
      message.success('个人信息更新成功！');
    } else {
        message.warn('请先登录才能编辑个人信息。');
        setAboutInfo(defaultAboutInfoTemplate);
    }
  };


  return ( 
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            loggedInUser ? (
              <Home posts={posts} loading={loading} error={error} loggedInUser={loggedInUser} onLogout={handleLogout} />
            ) : (
                <Auth
                  onLogin={handleLogin}
                  onRegister={handleRegister}
                  loggedInUser={loggedInUser}
                  onLogout={handleLogout}
                />
            )
          }
        />

        <Route
          path="/auth"
          element={
            <Auth
              onLogin={handleLogin}
              onRegister={handleRegister}
              loggedInUser={loggedInUser}
              onLogout={handleLogout}
            />
          }
        />

        <Route
          path="/post/:id"
          element={
              <PostDetail
                  posts={posts}
                  loggedInUser={loggedInUser}
                  onLogout={handleLogout}
              />
          }
        />
        <Route
            path="/archive"
            element={
                <Archive posts={posts} loading={loading} error={error} loggedInUser={loggedInUser} onLogout={handleLogout} />
            }
        />

        <Route
          path="/about"
          element={
            <About
              aboutInfo={aboutInfo}
              onUpdateAboutInfo={handleUpdateAboutInfo}
              loading={aboutLoading}
              canEdit={!!loggedInUser}
              onLogout={handleLogout}
              loggedInUser={loggedInUser}
            />
          }
        />

        <Route
          path="/write"
          element={
            loggedInUser ? (
              <WritePost
                onAddPost={handleAddPost}
                posts={posts}
                loggedInUser={loggedInUser}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />

        <Route
            path="*"
            element={
                <NotFound loggedInUser={loggedInUser} onLogout={handleLogout} />
            }
        />
      </Routes>
    </Router>
  );
}

export default App;
